import { motion } from "framer-motion";
import Fetcher from "../hooks/Fetcher";
import BlockRightImage from "../components/BlockRightImage";
import {
  CalendarDays,
  Factory,
  CircleUserRound,
  FileBox,
  MicVocal,
  ShieldIcon,
  UserCheck,
  UserStar,
  Vote,
} from "lucide-react";

const url =
  "https://eventos.utpl.edu.ec/jsonapi/node/congresos/3f9fbd42-3b1e-482e-9fba-d95433d5ef54" +
  "?include=field_imagen2.field_media_image" +
  "&fields[node--congresos]=title,field_texto_portada,field_imagen2,field_bloque1,field_bloque2,field_bloque3,field_bloque4,field_bloque5,field_bloque6," +
  "field_bloque7,field_bloque8,field_bloque9,field_bloque10,field_bloque11,field_bloque12,field_bloque13,field_bloque14,field_bloque15,field_bloque16,field_bloque17," +
  "field_bloque18,field_bloque19,field_bloque20,field_bloque21,field_bloque22,field_bloque23";

function Conferencia() {
  const DataDisplay = ({ data }) => {
    const bloques = [
      { html: data?.bloque4, icon: Vote },
      { html: data?.bloque5, icon: ShieldIcon },
      { html: data?.bloque6, icon: UserStar },
      { html: data?.bloque7, icon: CircleUserRound },
      { html: data?.bloque8, icon: MicVocal },
      { html: data?.bloque9, icon: UserCheck },
      { html: data?.bloque10, icon: FileBox },
    ];

    return (
      <div>
        <section id="inicio" className="relative overflow-hidden bg-gradient-to-br from-[#00365d] via-[#004270] to-[#005d9c] py-10">
          {/* Glow superior */}
          <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#febe10]/10 blur-3xl" />

          {/* Glow inferior */}
          <div className="absolute -bottom-56 -left-40 h-[28rem] w-[28rem] rounded-full bg-cyan-400/10 blur-3xl" />

          {/* Patrón de puntos */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          />

          <div className="relative max-w-6xl mx-auto px-6">
            <motion.div
              className="h-1 w-20 rounded-full bg-[#febe10] mb-8"
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: 80, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />

            <motion.div
              className="
                [&_h3]:text-[#febe10]
                [&_h3]:font-bold
                    [&_h2]:text-white
             [&_h2]:font-extrabold
                [&_h2]:max-w-3xl
                         [&_h2]:text-5xl
                
                [&_h4]:inline-block
                [&_h4]:bg-white
                [&_h4]:backdrop-blur-sm
                [&_h4]:text-[#004270]
                [&_h4]:uppercase
                [&_h4]:tracking-[0.18em]
                [&_h4]:text-sm
                [&_h4]:font-semibold
                [&_h4]:px-4
                [&_h4]:py-2
                [&_h4]:rounded-full
                [&_h4]:border
                [&_h4]:border-[#febe10]/30
                [&_h4]:shadow-lg
                [&_h4]:shadow-black/30
                [&_h4]:mb-6
                [&_h4]:mr-2
                [&_p]:max-w-2xl
                [&_p]:text-lg
                [&_p]:leading-relaxed
                [&_p]:text-white/90
                [&_p]:mt-4
                [&_p]:mb-10
              "
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              dangerouslySetInnerHTML={{
                __html: data?.texto_portada || "",
              }}
            />
          </div>
        </section>

        <section className="bg-slate-50 py-24">
          <div className="max-w-6xl px-6 m-auto">
            <BlockRightImage
              src={data.imagen2}
              content={
                <>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data?.bloque2 || "",
                    }}
                  />
                </>
              }
            ></BlockRightImage>
          </div>
        </section>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg max-w-4xl m-auto my-24"
        >
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#004270]">
              <CalendarDays className="h-7 w-7 text-[#febe10]" />
            </div>
          </div>

          <div
            dangerouslySetInnerHTML={{
              __html: data?.bloque1 || "",
            }}
          />
        </motion.div>

        <section id="lineas" className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div
              className=" text-center   "
              dangerouslySetInnerHTML={{
                __html: data?.bloque3 || "",
              }}
            />

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {bloques.map(({ html, icon: Icon }, index) => {
                // Solo renderizamos la tarjeta si hay contenido HTML
                if (!html) return null;

                return (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{
                      y: -8,
                      transition: { duration: 0.2 },
                    }}
                    className="group rounded-2xl bg-white p-8 shadow-md border border-slate-200 hover:border-[#febe10] hover:shadow-xl transition-all"
                  >
                    <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#004270] text-[#febe10] transition-all group-hover:scale-110 group-hover:rotate-6">
                      <Icon size={30} />
                    </div>

                    <div
                      className="
    
    [&_p]:text-slate-600
    [&_p]:leading-relaxed
    [&_p]:text-lg
    [&_p]:mb-4

    [&_p_strong]:font-semibold
    [&_p_strong]:text-[#004270]
    [&_p_strong]:block
    [&_p_strong]:mb-2

    [&_ul]:list-disc
    [&_ul]:pl-6
    [&_ul]:mb-4

    [&_ol]:list-decimal
    [&_ol]:pl-6
    [&_ol]:mb-4

    [&_li]:text-slate-600
    [&_li]:leading-snug
    [&_li]:text-sm
    [&_li]:mb-2
      [&_h2]:text-lg
    marker:text-[#004270]
  "
                      dangerouslySetInnerHTML={{
                        __html: html,
                      }}
                    />
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="inscripcion" className="py-24 bg-[#004270]">
          <div className="max-w-6xl mx-auto px-6">
            <div
              className="[&_h2]:text-white  [&_p]:text-white text-center btn-primary "
              dangerouslySetInnerHTML={{
                __html: data?.bloque13 || "",
              }}
            />
          </div>
        </section>

        <section id="call-for-papers" className="py-24  ">
          <div className="max-w-5xl mx-auto px-6">
            <div
              className="      btn-primary "
              dangerouslySetInnerHTML={{
                __html: data?.bloque11 || "",
              }}
            />
          </div>
        </section>


        
        <section className="py-24 bg-slate-50  ">
          <div className="max-w-5xl mx-auto px-6">
         
              <div
              className="      btn-primary "
              dangerouslySetInnerHTML={{
                __html: data?.bloque12 || "",
              }}
            />
          </div>
        </section>
      </div>
    );
  };

  return <Fetcher url={url}>{(data) => <DataDisplay data={data} />}</Fetcher>;
}

export default Conferencia;
